'use client';

import { formatFitLabel } from '@/components/shared/gtmFitLabel';
import { PanelSectionLabel } from '@/components/shared/PanelSectionLabel';
import { Fragment, useState } from 'react';
import { Target } from 'lucide-react';
import type {
  AccountPriority,
  AccountPriorityRawReason,
  ConsolidatedAccountPriorityReason,
} from '@/types/prospects';
import { accountPriorityBandStyle } from './accountPriorityBand';

/** Points shown before the reader has to ask for the rest. */
const COLLAPSED_REASON_COUNT = 2;

/*
 * Three type tiers, and only three.
 *
 *   text-sm  — prose a seller reads to decide: the band sentence, the
 *              sub-scores, the reason bullets. Same tier as the signal and
 *              company reasoning this card sits beside in the task panels.
 *   text-xs  — captions that qualify the tier above: unscored notes, routing,
 *              data-status caveats.
 *   11px     — the eyebrow label and the provenance footer.
 *
 * The card previously ran its own ramp (12.5 / 11.5 / 10.5) and then, briefly,
 * the whole of it at 12px and below. Both versions failed the same way: a 26px
 * score on top of six sizes crammed between 10 and 12.5px is not a hierarchy,
 * it is one big number and a block of fine print. Steps have to be far enough
 * apart to read as steps, and the body tier has to match the surrounding panel
 * rather than sit a size under it.
 */

/**
 * Band guidance. The seller's question is "do I work this now", and a number
 * alone does not answer it — the band does, so it is stated in words beside the
 * score rather than left to be inferred from the colour.
 */
const BAND_HINTS: Record<string, string> = {
  High: 'Strong fit and active intent. Work this today.',
  Medium: 'Worth a touch. Fit is there, intent is still building.',
  Low: 'Fit or intent is thin. Nurture rather than call.',
};

/**
 * What an unscored account is actually saying, which is not "something broke".
 *
 * Roughly 96% of the internal workspace's projections carry `not_available`, and
 * every one of them was processed successfully — there was simply no ICP
 * assessment and no qualifying buyer behaviour to score. Printing `Unknown` in
 * 26px bold over that reads as a failed computation, and reading it that way is
 * the single most common piece of feedback on this card. These sentences name
 * the state instead, and separate the three that look identical today: nothing
 * found, not finished yet, and genuinely failed.
 *
 * The outbound line belongs here rather than in a tooltip because the question
 * it answers — "we have emailed this account fifty times, why is it blank" — is
 * asked at exactly this moment. Seller activity is deliberately not an input:
 * every CRM Momentum contributor is buyer-side (reply, meeting, opportunity with
 * fresh buyer engagement, closed-lost re-engagement), and enrollment, sends and
 * calls contribute nothing.
 */
const UNSCORED_HINTS: Record<string, string> = {
  not_available: 'No recent buyer evidence. Sending outbound or enrolling this account does not raise its priority.',
  pending: 'Calculating. This account has not been scored yet.',
  // No score row and nothing queued (mb-server not_scored). Saying "Calculating"
  // here was a promise nothing was going to keep -- and the sentence that replaced it
  // ("Not scored yet. No Account Priority has been calculated for this account.") said the
  // same thing twice and left the seller nothing to do with it. Every other hint here ends
  // on the consequence; this state's consequence is that no score is coming on its own,
  // which `error`'s "retried automatically" is precisely NOT.
  not_scored: 'Not scored, and nothing is queued to score it. Judge this one on the signal itself.',
  stale: 'Calculating. New evidence arrived and the score is being recomputed.',
  error: 'Scoring failed for this account. It is retried automatically.',
};

const DEFAULT_UNSCORED_HINT = 'No recent buyer evidence. Sending outbound or enrolling this account does not raise its priority.';

/**
 * The one sentence that tells a seller what the band means for them: the band
 * guidance for a scored account, the named unscored state otherwise.
 */
export const priorityVerdict = (priority: AccountPriority | null): string => {
  const band = priority?.label && priority.label !== 'Unknown' ? priority.label : null;
  if (priority?.score != null) return (band && BAND_HINTS[band]) || '';
  return UNSCORED_HINTS[priority?.dataStatus || ''] || DEFAULT_UNSCORED_HINT;
};

/** Evaluator reasoning arrives as strings; CRM reasons arrive as objects. */
const rawReasonText = (reason: AccountPriorityRawReason): string => {
  if (typeof reason === 'string') return reason;
  return reason?.label || reason?.code || '';
};

const displayTimestamp = (value: unknown) => {
  if (typeof value !== 'string' && !(value instanceof Date)) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * List endpoints no longer ship the evidence array — it averaged 2 KB and
 * peaked at 10 KB per row, and this max() was the only thing any list consumer
 * read from it. The backend precomputes the same value into evidenceSummary.
 * The array is still scanned when present, so the detail endpoints (which do
 * send it) and rows written before this shipped both keep working.
 */
export const latestEvidenceAt = (priority: AccountPriority | null): string | Date | null => {
  const precomputed = priority?.evidenceSummary?.latest_evidence_at;
  if (typeof precomputed === 'string') return precomputed;
  // occurred_at only. An evaluation is not an event, and folding evaluated_at
  // into this max() made every re-scoring pass look like fresh account
  // activity — on the server side that put 810 of 941 accounts an average of
  // 261 hours ahead of their own newest signal before it was fixed. This
  // fallback only runs for rows written before evidenceSummary existed, but it
  // must not disagree with the precomputed value above it.
  const times = (priority?.evidence || [])
    .map((item) => item.occurred_at)
    .map((value) => (typeof value === 'string' ? new Date(value).getTime() : Number.NaN))
    .filter(Number.isFinite);
  return times.length > 0 ? new Date(Math.max(...times)) : null;
};

const latestEvidenceTimestamp = (priority: AccountPriority | null) => displayTimestamp(latestEvidenceAt(priority));

const routingText = (priority: AccountPriority | null) => {
  const eligibility = priority?.routing?.outreachEligibility;
  if (eligibility === 'customer') return 'Customer account: route to the customer or expansion lane.';
  if (eligibility === 'active_opportunity') return 'Active opportunity: route to the current deal owner.';
  if (eligibility === 'ignored') return 'This account is ignored for net-new outreach.';
  if (eligibility === 'all_contacts_suppressed') return 'The score is valid, but no known contact is currently outreach-eligible.';
  return null;
};

/**
 * Prefer the consolidated explanation. Accounts scored before this shipped have
 * an empty list until their next recompute, so fall back to the per-component
 * reasons rather than showing a card with no explanation at all.
 */
export const explanationPoints = (priority: AccountPriority | null): ConsolidatedAccountPriorityReason[] => {
  const consolidated = (priority?.consolidatedReasons || []).filter((point) => point?.text?.trim());
  if (consolidated.length > 0) {
    // Saved explanations retain the old copy until the account is recomputed.
    return consolidated.map((point) => ({
      ...point,
      text: point.text === 'Activity from this company does not count as buyer intent: Jev judged it a competitor of this workspace.'
        ? 'Activity from this company does not count as buyer intent: AI identified it as a likely competitor.'
        : point.text,
    }));
  }
  return [
    ...(priority?.reasons?.pureIcpFit || []).map((reason) => ({ text: rawReasonText(reason), kind: 'fit' as const })),
    ...(priority?.reasons?.buyerIntent || []).map((reason) => ({ text: rawReasonText(reason), kind: 'intent' as const })),
    ...(priority?.reasons?.crmMomentum || []).map((reason) => ({ text: rawReasonText(reason), kind: 'crm' as const })),
  ].filter((point) => point.text.trim());
};

/**
 * Statuses that mean the component genuinely has no value.
 *
 * `degraded` and `stale` are deliberately NOT here. Both are set when a lookup
 * was slow or a batch refresh failed, while the component still carries a real
 * number the headline score was computed from — treating them as absent made the
 * card print "Buyer intent not scored yet" directly above a score that visibly
 * contains 0.55 × that intent. The dataStatus caveat line already reports
 * staleness; this predicate answers a narrower question.
 */
const ABSENT_COMPONENT_STATUSES = ['unavailable', 'not_configured'];

const isComponentScored = (priority: AccountPriority | null, key: 'pureIcpFit' | 'buyerIntent' | 'crmMomentum') => {
  const status = priority?.componentStatus?.[key];
  if (status && ABSENT_COMPONENT_STATUSES.includes(status)) return false;
  return priority?.components?.[key] != null;
};

/**
 * Buyer intent renders on the coarser /10 scale the design comps specify.
 *
 * Storage stays 0-100; this is display only. Plain division rounds a measured 4
 * to "0", which collides head-on with the invariant that a model-produced zero
 * is authoritative and distinct from Unknown — the card could not then
 * distinguish "no intent at all" from "a little intent". Anything above zero
 * floors at 1.
 */
const toTenPointScale = (value: number) => (value <= 0 ? 0 : Math.max(1, Math.round(value / 10)));

export type AccountPriorityComponentValue = {
  key: 'pureIcpFit' | 'buyerIntent' | 'crmMomentum';
  label: string;
  value: number;
  scale: string;
};

/**
 * The scored components in display order and display scale — the one list this
 * card, the Signals detail panel and any tooltip print, so "ICP fit 60/100 ·
 * Buyer intent 3/10 · CRM momentum 14/20" reads the same on every surface. A CRM
 * momentum of 0 is a real measurement (connected, nothing moving) but has no
 * contribution to show, so it is left out of the composition line.
 */
export const accountPriorityComponentValues = (priority: AccountPriority | null): AccountPriorityComponentValue[] => {
  const values: AccountPriorityComponentValue[] = [];
  if (isComponentScored(priority, 'pureIcpFit')) {
    values.push({ key: 'pureIcpFit', label: 'ICP fit', value: Math.round(priority!.components.pureIcpFit as number), scale: '/100' });
  }
  if (isComponentScored(priority, 'buyerIntent')) {
    values.push({ key: 'buyerIntent', label: 'Buyer intent', value: toTenPointScale(priority!.components.buyerIntent as number), scale: '/10' });
  }
  if (isComponentScored(priority, 'crmMomentum') && (priority?.components?.crmMomentum ?? 0) > 0) {
    values.push({ key: 'crmMomentum', label: 'CRM momentum', value: priority!.components.crmMomentum, scale: '/20' });
  }
  return values;
};

/**
 * A sub-score is a number, not a meter.
 *
 * These were bars until Sunder pointed out the obvious: ICP fit is /100 and
 * buyer intent is /10, so two bars side by side invite a length comparison that
 * means nothing. Rendering them inline as plain values removes the false
 * affordance and the visual noise with it.
 */
const ComponentValue = ({ label, value, scale }: { label: string; value: number; scale: string }) => (
  <span className="whitespace-nowrap text-sm text-slate-600">
    {label}
    {' '}
    <span className="font-semibold tabular-nums text-slate-900">{value}</span>
    <span className="tabular-nums text-slate-400">{scale}</span>
  </span>
);

export const AccountPriorityCard = ({
  priority,
  enabled = true,
  standalone = true,
  legacyFit = null,
}: {
  priority: AccountPriority | null;
  enabled?: boolean;
  /**
   * The GTM Fit a list row quotes while the account has no Account Priority
   * (AccountScoreCell's `gtm_fit` legacy). Named here so the card does not
   * read "not scored" beside a row that shows "95 FIT".
   */
  legacyFit?: { score: number | null | undefined; label?: string | null } | null;
  /**
   * Whether the card draws its own panel chrome — gutter, background and the
   * rule that separates it from the next section.
   *
   * A shared card cannot know its container's gutter, and guessing produced the
   * same defect three times: `px-5` doubled the inset in the Signals sheet,
   * fell 12px short of `px-responsive` in the task panels, and missed the
   * `px-6` sheets by 4px. When false the card renders as bare content and the
   * caller supplies gutter, spacing and separator to match its own siblings —
   * which is the only place that information exists.
   */
  standalone?: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);
  if (!enabled) return null;

  const band = priority?.label && priority.label !== 'Unknown' ? priority.label : null;
  const bandStyle = accountPriorityBandStyle(band);
  const score = priority?.score ?? null;

  const fitScored = isComponentScored(priority, 'pureIcpFit');
  const intentScored = isComponentScored(priority, 'buyerIntent');
  // Only a genuinely absent CRM earns the "no CRM connected" copy. A degraded
  // or stale status means one refresh failed on a connected CRM, and asserting
  // otherwise writes a false statement about the customer's own systems.
  const crmUnavailable = priority?.componentStatus?.crmMomentum === 'unavailable'
    || (!priority?.componentStatus?.crmMomentum && priority?.components?.crmMomentum == null);
  const componentValues = accountPriorityComponentValues(priority);

  // An unscored component is named rather than rendered as an "Unknown" tile. A
  // tile reads like a measured result; the note reads like the absence it is,
  // which is what a seller needs in order to judge the number.
  const unscored = [
    !fitScored ? 'ICP fit' : null,
    !intentScored ? 'Buyer intent' : null,
    crmUnavailable ? 'CRM momentum' : null,
  ].filter(Boolean) as string[];
  const unscoredLabel = unscored.length > 1
    ? `${unscored.slice(0, -1).join(', ')} and ${unscored[unscored.length - 1]}`
    : unscored[0];

  const points = explanationPoints(priority);
  const visiblePoints = expanded ? points : points.slice(0, COLLAPSED_REASON_COUNT);
  const hiddenCount = Math.max(0, points.length - COLLAPSED_REASON_COUNT);

  const summary = priority?.evidenceSummary;
  const sessionCount = summary?.website_session_count || 0;
  const hasEvidenceChip = Boolean(summary?.primary_signal_label) && sessionCount > 0;
  const route = routingText(priority);
  const latestEvidenceAt = latestEvidenceTimestamp(priority);
  const calculatedAt = displayTimestamp(priority?.calculatedAt);
  const scoreVersion = priority?.scoreVersion?.replace(/^account-priority-/, '') || null;

  return (
    <section
      className={standalone ? 'border-b border-slate-100 bg-white px-5 py-4' : undefined}
      aria-label="Account Priority explanation"
    >
      {/* "Account Priority" keeps its capitals: it is the feature's name, spelled that way in
          the column header, the row tooltips and the sr-only text. What changed is the style —
          it was 11px/600 in capitals with tracking, and it is now the one section label. */}
      <PanelSectionLabel icon={<Target className="h-3.5 w-3.5 flex-shrink-0 text-mb-blue-600" aria-hidden="true" />}>
        Account Priority
      </PanelSectionLabel>

      <div className="mt-2 flex items-baseline gap-2">
        {/*
          An em-dash, not the word Unknown. This is the same glyph the Tasks and
          Prospects lists already use for an unscored row, and unlike a bold
          English word it does not assert that anything went wrong. The sentence
          below carries the actual state.
        */}
        <span className="text-[26px] font-bold leading-none tabular-nums text-slate-900">
          {score == null ? '—' : Math.round(score)}
        </span>
        {score != null && <span className="text-sm font-medium text-slate-400">/100</span>}
        {band && (
          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${bandStyle.pill}`}>{band}</span>
        )}
      </div>

      {/*
        Verdict group: the number, the scale cue and the sentence that says what
        to do about it are one thought, so they sit close together and the next
        group opens on a wider gap. Before this the whole card used a near-uniform
        ~10px rhythm and read as eight unrelated lines.
      */}
      {score != null && (
        <div className="mt-2 h-[3px] w-[140px] overflow-hidden rounded-full bg-slate-100">
          <div className={`h-full rounded-full ${bandStyle.bar}`} style={{ width: `${Math.max(0, Math.min(100, score))}%` }} />
        </div>
      )}

      {band && (
        <p className="mt-2 text-sm leading-[1.5] text-slate-600">{BAND_HINTS[band]}</p>
      )}

      {score == null && (
        <p className="mt-2 text-sm leading-[1.5] text-slate-600">{priorityVerdict(priority)}</p>
      )}

      {score == null && legacyFit?.score != null && (
        <p className="mt-1 text-xs leading-[1.5] text-slate-500">
          {`The list shows GTM Fit ${Math.round(legacyFit.score)}/100${legacyFit.label ? ` (${formatFitLabel(legacyFit.label)})` : ''} until this account has an Account Priority.`}
        </p>
      )}

      {/* Composition group: one inline row, "ICP fit 60/100 · Buyer intent 3/10". */}
      {componentValues.length > 0 && (
        <div className="mt-4 flex flex-wrap items-baseline gap-x-2 gap-y-1">
          {componentValues.map((component, index) => (
            <Fragment key={component.key}>
              {index > 0 && <span className="text-slate-300">·</span>}
              <ComponentValue label={component.label} value={component.value} scale={component.scale} />
            </Fragment>
          ))}
        </div>
      )}

      {/*
        A caption on the row above, not a peer paragraph — it qualifies those
        numbers and is meaningless on its own.
      */}
      {unscored.length > 0 && (
        <p className="mt-1.5 text-xs text-slate-500">
          {`${unscoredLabel} not scored`}
          {unscored.length === 1 && crmUnavailable
            ? ' — no CRM connected for this account.'
            : ' yet.'}
        </p>
      )}

      {/*
        Evidence group. The signal chip is evidence, so it belongs with the
        reasons it supports rather than floating between the sub-scores and the
        rule that used to separate them. Rendered whenever either half exists,
        so an account with a chip and no reasons still shows it.
      */}
      {(hasEvidenceChip || points.length > 0) && (
        <div className="mt-4 border-t border-slate-100 pt-4">
          {hasEvidenceChip && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-mb-blue-50 px-2 py-0.5 text-[11px] font-semibold text-mb-blue-700">
                {summary!.primary_signal_label}
              </span>
              <span className="text-xs text-slate-500">
                {sessionCount === 1 ? '1 session' : `${sessionCount} sessions`}
                {summary!.window_days ? ` in the last ${summary!.window_days} days` : ''}
              </span>
            </div>
          )}
          {points.length > 0 && (
            <ul
              className={`space-y-2 text-sm leading-[1.55] text-slate-700 ${hasEvidenceChip ? 'mt-3' : ''}`}
            >
              {visiblePoints.map((point, index) => (
                <li key={`${point.kind}-${point.text.slice(0, 40)}-${index}`} className="flex gap-2.5">
                  <span
                    className={`mt-[7px] h-1 w-1 flex-none rounded-full ${point.kind === 'gap' ? 'bg-slate-300' : 'bg-slate-400'}`}
                    aria-hidden="true"
                  />
                  <span>{point.text}</span>
                </li>
              ))}
            </ul>
          )}
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((open) => !open)}
              aria-expanded={expanded}
              className="mt-2.5 text-sm font-medium text-mb-blue-600 hover:text-mb-blue-700"
            >
              {expanded ? 'Show less' : `Show ${hiddenCount} more reason${hiddenCount === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      )}

      {route && <p className="mt-3 text-xs font-medium text-slate-600">{route}</p>}
      {/*
        `degraded` is not a fault and must not be coloured like one. The backend
        sets it whenever exactly one of fit or intent has a value — a designed,
        expected outcome that covers 294 of the internal workspace's 969 scored
        accounts, or 30% of them. Amber over a third of the scored population
        trains a seller to ignore the colour, and there are no lookup failures
        behind it: production logged none in twelve hours. Naming which half is
        missing is the useful part, because that is what the seller has to go
        and find.
      */}
      {priority?.dataStatus === 'degraded' && (
        <p className="mt-2 text-xs text-slate-500">
          {!fitScored
            ? 'Scored on buyer intent only. No ICP fit assessment yet, which holds this score down until the account is qualified.'
            : 'Scored on ICP fit only. No qualifying buyer activity in the current evidence window.'}
        </p>
      )}
      {priority?.dataStatus === 'stale' && score != null && (
        <p className="mt-2 text-xs text-slate-500">Refreshing after new evidence; the prior score is shown.</p>
      )}
      {priority && (latestEvidenceAt || calculatedAt || scoreVersion) && (
        <p
          className="mt-4 text-[11px] text-slate-400"
          title={`Score version ${priority.scoreVersion}${priority.scoreRevision ? `, revision ${priority.scoreRevision}` : ''}`}
        >
          {latestEvidenceAt ? `Latest evidence ${latestEvidenceAt}` : null}
          {latestEvidenceAt && calculatedAt ? ' · ' : null}
          {calculatedAt ? `Calculated ${calculatedAt}` : null}
          {(latestEvidenceAt || calculatedAt) && scoreVersion ? ' · ' : null}
          {scoreVersion ? `Score ${scoreVersion}` : null}
        </p>
      )}
    </section>
  );
};
